import { FC, ReactNode, useState, useRef, useEffect } from 'react';
import { UncontrolledTooltipProps, Tooltip } from 'reactstrap';
import { ErrorBoundary } from '@/library/components/general/ErrorBoundary';

import SvgRender from '@/library/components/general/SvgRender';
import info from '@/library/assets/svg/common/info.svg';
import useTooltipID from '@/library/utils/hooks/useTooltipID';

export interface InformationProps extends Omit<UncontrolledTooltipProps, 'target'> {
  message: ReactNode | string;
  className?: string;
  wrapperClassName?: string;
  tooltipClassname?: string;
  tooltipOffset?: number[];
  svgClassname?: string;
  svgStyle?: Record<string, unknown>;
  hoverIcon?: string;
  svgIcon?: string;
  placement?: UncontrolledTooltipProps['placement'];
  renderComponent?: (isHovering: boolean) => ReactNode;
}

const Information: FC<InformationProps> = ({
  message,
  className = '',
  tooltipClassname = '',
  svgClassname = '',
  svgStyle,
  renderComponent,
  hoverIcon,
  placement = 'top',
  hideArrow,
  svgIcon,
  wrapperClassName = '',
  tooltipOffset
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const iconRef = useRef<HTMLDivElement>(null);
  const { avoidRender, tooltipID } = useTooltipID('information-tooltip');
  const [isTooltipReady, setIsTooltipReady] = useState(false);

  useEffect(() => {
    if (!avoidRender && iconRef.current) {
      setIsTooltipReady(true);
    }
  }, [avoidRender]);

  useEffect(() => {
    return () => {
      setIsTooltipReady(false);
    };
  }, []);

  return (
    <div
      className={`d-inline-flex information-tooltip ${wrapperClassName}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div ref={iconRef} id={tooltipID} className={`${className} d-flex cursor-pointer`}>
        {renderComponent ? (
          renderComponent(isHovering)
        ) : (
          <SvgRender
            src={isHovering && hoverIcon ? hoverIcon : svgIcon || info}
            style={svgStyle || { width: 18, height: 18 }}
            className={`${svgClassname ? svgClassname : ''} pe-none`}
          />
        )}
      </div>

      {isHovering && isTooltipReady && (
        <ErrorBoundary fallback={null} notifyBugsnag={false}>
          <Tooltip
            isOpen={isHovering}
            fade={false}
            target={tooltipID}
            innerClassName={tooltipClassname || 'show-more-tooltip'}
            hideArrow={hideArrow}
            placement={placement}
            boundariesElement="window"
            offset={tooltipOffset}
          >
            {message}
          </Tooltip>
        </ErrorBoundary>
      )}
    </div>
  );
};

export default Information;
